import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { Alert, Modal, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";

const DEFAULT_CENTER = { lat: 24.7136, lng: 46.6753 };
const OVERLAP_OFFSET_DEGREES = 0.00018;
const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
const GOOGLE_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID";

export type MapDriver = {
  driverId?: number | null;
  name?: string | null;
  phoneNumber?: string | null;
  vehicleNumber?: string | null;
  vehicleType?: string | null;
  driverStatus?: string | null;
  isOnline?: boolean | null;
  hasUncompletedDeliveryRequest?: boolean | null;
  distanceKm?: number | null;
  latitude?: number | null;
  longitude?: number | null;
} | null;

type UnassignedOrderMapProps = {
  storeName?: string | null;
  storeLatitude?: number | null;
  storeLongitude?: number | null;
  drivers?: MapDriver[] | null;
  height?: number | string;
  assigningDriverId?: number | null;
  onAssignDriver?: (driverId: number) => void | Promise<void>;
};

type LatLng = { lat: number; lng: number };

type DriverMapPoint = {
  key: string | number;
  position: LatLng;
  originalPosition: LatLng;
  driver: NonNullable<MapDriver>;
  overlapIndex: number;
  overlapCount: number;
};

type MarkerLike = {
  map?: google.maps.Map | null;
  setMap?: (map: google.maps.Map | null) => void;
};

function toCoordinate(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function getCoordinateKey(latitude: number, longitude: number) {
  return `${latitude.toFixed(5)},${longitude.toFixed(5)}`;
}

function spreadOverlappingDrivers(
  drivers: MapDriver[] | null | undefined,
): DriverMapPoint[] {
  const validDrivers = (drivers ?? []).flatMap((driver, index) => {
    const latitude = toCoordinate(driver?.latitude);
    const longitude = toCoordinate(driver?.longitude);

    if (!driver || latitude === null || longitude === null) {
      return [];
    }

    return [
      {
        key: driver.driverId ?? index,
        latitude,
        longitude,
        driver,
      },
    ];
  });

  const groups = new Map<string, typeof validDrivers>();

  validDrivers.forEach((item) => {
    const key = getCoordinateKey(item.latitude, item.longitude);
    const group = groups.get(key) ?? [];
    group.push(item);
    groups.set(key, group);
  });

  return Array.from(groups.values()).flatMap((group) => {
    const overlapCount = group.length;

    return group.map((item, overlapIndex) => {
      let latitude = item.latitude;
      let longitude = item.longitude;

      if (overlapCount > 1) {
        const angle = (2 * Math.PI * overlapIndex) / overlapCount;
        const radius = OVERLAP_OFFSET_DEGREES;
        latitude = item.latitude + radius * Math.cos(angle);
        longitude = item.longitude + radius * Math.sin(angle);
      }

      return {
        key: item.key,
        position: { lat: latitude, lng: longitude },
        originalPosition: { lat: item.latitude, lng: item.longitude },
        driver: item.driver,
        overlapIndex,
        overlapCount,
      };
    });
  });
}

function formatYesNo(
  value: boolean | null | undefined,
  yes: string,
  no: string,
) {
  if (value === null || value === undefined) return "-";
  return value ? yes : no;
}

function UnassignedOrderMap({
  storeName,
  storeLatitude,
  storeLongitude,
  drivers = [],
  height = 420,
  assigningDriverId = null,
  onAssignDriver,
}: UnassignedOrderMapProps) {
  const { t } = useTranslation();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const markersRef = useRef<MarkerLike[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const onAssignDriverRef = useRef(onAssignDriver);
  const assignDomListenerRef = useRef<google.maps.MapsEventListener | null>(
    null,
  );

  useEffect(() => {
    onAssignDriverRef.current = onAssignDriver;
  }, [onAssignDriver]);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  const { isLoaded, loadError } = useJsApiLoader({
    id: "unassigned-order-google-map",
    googleMapsApiKey: apiKey,
    libraries: ["marker"],
  });

  const storePoint = useMemo<LatLng | null>(() => {
    const latitude = toCoordinate(storeLatitude);
    const longitude = toCoordinate(storeLongitude);
    if (latitude === null || longitude === null) return null;
    return { lat: latitude, lng: longitude };
  }, [storeLatitude, storeLongitude]);

  const driverPoints = useMemo(
    () => spreadOverlappingDrivers(drivers),
    [drivers],
  );

  const allPoints = useMemo(
    () => [
      ...(storePoint ? [storePoint] : []),
      ...driverPoints.map((point) => point.position),
    ],
    [driverPoints, storePoint],
  );

  const center = allPoints[0] ?? DEFAULT_CENTER;

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => {
      if (typeof marker.setMap === "function") {
        marker.setMap(null);
      } else {
        marker.map = null;
      }
    });
    markersRef.current = [];
    if (assignDomListenerRef.current) {
      google.maps.event.removeListener(assignDomListenerRef.current);
      assignDomListenerRef.current = null;
    }
    infoWindowRef.current?.close();
  }, []);

  const fitMapToPoints = useCallback(
    (nextMap: google.maps.Map | null) => {
      if (!nextMap || !window.google?.maps || !allPoints.length) return;

      if (allPoints.length === 1) {
        nextMap.setCenter(allPoints[0]);
        nextMap.setZoom(15);
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();
      allPoints.forEach((point) => bounds.extend(point));
      nextMap.fitBounds(bounds, {
        top: 48,
        right: 48,
        bottom: 48,
        left: 48,
      });
    },
    [allPoints],
  );

  const onLoad = useCallback((nextMap: google.maps.Map) => {
    setMap(nextMap);
  }, []);

  const onUnmount = useCallback(() => {
    clearMarkers();
    setMap(null);
  }, [clearMarkers]);

  useEffect(() => {
    if (!map || !isLoaded || !window.google?.maps) return;

    let cancelled = false;

    const renderMarkers = async () => {
      clearMarkers();
      window.google.maps.event.trigger(map, "resize");
      fitMapToPoints(map);

      const infoWindow =
        infoWindowRef.current ?? new window.google.maps.InfoWindow();
      infoWindowRef.current = infoWindow;

      const markerLibrary = (await google.maps.importLibrary(
        "marker",
      )) as google.maps.MarkerLibrary;

      if (cancelled) return;

      const { AdvancedMarkerElement, PinElement } = markerLibrary;
      const yesLabel = t("label.yes");
      const noLabel = t("label.no");

      const openInfo = (position: LatLng, html: string, onDomReady?: () => void) => {
        if (assignDomListenerRef.current) {
          google.maps.event.removeListener(assignDomListenerRef.current);
          assignDomListenerRef.current = null;
        }

        infoWindow.setContent(html);
        infoWindow.setPosition(position);
        infoWindow.open({ map });

        if (onDomReady) {
          assignDomListenerRef.current = google.maps.event.addListenerOnce(
            infoWindow,
            "domready",
            onDomReady,
          );
        }
      };

      if (storePoint) {
        const pin = new PinElement({
          background: "#1677ff",
          borderColor: "#ffffff",
          glyphColor: "#ffffff",
          scale: 1.2,
        });

        const storeMarker = new AdvancedMarkerElement({
          map,
          position: storePoint,
          title: storeName || t("label.store.name"),
          content: pin.element,
          zIndex: 1000,
        });

        storeMarker.addListener("click", () => {
          openInfo(
            storePoint,
            `
              <div>
                <strong>${escapeHtml(storeName || t("label.store.name"))}</strong>
                <div>${escapeHtml(t("label.order.store_location"))}</div>
              </div>
            `,
          );
        });

        markersRef.current.push(storeMarker);
      }

      driverPoints.forEach((point) => {
        const pin = new PinElement({
          background: "#52c41a",
          borderColor: "#ffffff",
          glyph:
            point.overlapCount > 1 ? String(point.overlapIndex + 1) : undefined,
          glyphColor: "#ffffff",
          scale: 1.1,
        });

        const driverMarker = new AdvancedMarkerElement({
          map,
          position: point.position,
          title: point.driver.name || String(point.driver.driverId ?? ""),
          content: pin.element,
          zIndex: 900 + point.overlapIndex,
        });

        driverMarker.addListener("click", () => {
          const driverId = point.driver.driverId;
          const assignButtonId = `assign-driver-${driverId ?? point.key}`;
          const isAssigning = assigningDriverId === driverId;
          const canAssign =
            typeof driverId === "number" && Number.isFinite(driverId);

          const overlapText =
            point.overlapCount > 1
              ? `<div>${escapeHtml(
                  t("label.order.overlapping_drivers", {
                    current: point.overlapIndex + 1,
                    total: point.overlapCount,
                  }),
                )}</div>`
              : "";

          const assignButton = canAssign
            ? `<button
                  id="${assignButtonId}"
                  type="button"
                  style="
                    margin-top: 10px;
                    padding: 6px 12px;
                    border: none;
                    border-radius: 6px;
                    background: #1677ff;
                    color: #fff;
                    cursor: ${isAssigning ? "not-allowed" : "pointer"};
                    opacity: ${isAssigning ? 0.7 : 1};
                  "
                  ${isAssigning ? "disabled" : ""}
                >
                  ${escapeHtml(
                    isAssigning
                      ? t("label.loading")
                      : t("button.assign_driver"),
                  )}
                </button>`
            : "";

          openInfo(
            point.position,
            `
              <div style="min-width:180px">
                <div><strong>${escapeHtml(point.driver.name || "-")}</strong></div>
                ${overlapText}
                <div>${escapeHtml(t("label.driver.id"))}: ${escapeHtml(
                  String(point.driver.driverId ?? "-"),
                )}</div>
                <div>${escapeHtml(t("label.phone"))}: ${escapeHtml(
                  point.driver.phoneNumber || "-",
                )}</div>
                <div>${escapeHtml(t("label.driver.status"))}: ${escapeHtml(
                  point.driver.driverStatus || "-",
                )}</div>
                <div>${escapeHtml(t("label.driver.is_online"))}: ${escapeHtml(
                  formatYesNo(point.driver.isOnline, yesLabel, noLabel),
                )}</div>
                <div>${escapeHtml(t("label.order.distance_km"))}: ${
                  point.driver.distanceKm === null ||
                  point.driver.distanceKm === undefined
                    ? "-"
                    : Number(point.driver.distanceKm).toFixed(2)
                }</div>
                <div>${escapeHtml(t("label.driver.vehicle_type"))}: ${escapeHtml(
                  point.driver.vehicleType || "-",
                )}</div>
                <div>${escapeHtml(t("label.driver.vehicle_number"))}: ${escapeHtml(
                  point.driver.vehicleNumber || "-",
                )}</div>
                <div>${escapeHtml(
                  t("label.order.has_uncompleted_delivery_request"),
                )}: ${escapeHtml(
                  formatYesNo(
                    point.driver.hasUncompletedDeliveryRequest,
                    yesLabel,
                    noLabel,
                  ),
                )}</div>
                ${assignButton}
              </div>
            `,
            () => {
              if (!canAssign || typeof driverId !== "number") return;

              const button = document.getElementById(assignButtonId);
              button?.addEventListener("click", () => {
                Modal.confirm({
                  title: t("label.order.assign_driver_confirm", {
                    driver: point.driver.name || driverId,
                  }),
                  okText: t("button.confirm"),
                  cancelText: t("button.cancel"),
                  onOk: async () => {
                    await onAssignDriverRef.current?.(driverId);
                    infoWindow.close();
                  },
                });
              });
            },
          );
        });

        markersRef.current.push(driverMarker);
      });
    };

    void renderMarkers();

    return () => {
      cancelled = true;
      clearMarkers();
    };
  }, [
    assigningDriverId,
    clearMarkers,
    driverPoints,
    fitMapToPoints,
    isLoaded,
    map,
    storeName,
    storePoint,
    t,
  ]);

  if (!apiKey) {
    return (
      <Alert
        type="warning"
        showIcon
        message={t("label.order.google_maps_api_key_missing")}
      />
    );
  }

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        message={t("error.failed_to_load")}
        description={loadError.message}
      />
    );
  }

  if (!isLoaded) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        {t("label.order.map_markers_summary", {
          store: storePoint ? 1 : 0,
          drivers: driverPoints.length,
        })}
      </Typography.Text>
      <div style={{ width: "100%", height, borderRadius: 8, overflow: "hidden" }}>
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={center}
          zoom={15}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            mapId: GOOGLE_MAP_ID,
            fullscreenControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            clickableIcons: false,
          }}
          onClick={() => infoWindowRef.current?.close()}
        />
      </div>
    </div>
  );
}

export default UnassignedOrderMap;
